/**
 * What `/fly` shows while the scene is coming up: one 320×200 shell frame with
 * a raised plate, the phase the boot is on, and a bar that is **filled from the
 * measurement** rather than animated (`boot-progress.ts`).
 *
 * Built from the shell's own vocabulary — `plate-chrome.ts`'s two-ring raised
 * plate and sunken well, `ps-palette.ts`'s tan and burnt-orange ramps,
 * `FONT1`/`FONT3` — so the screen between the briefing and the cockpit belongs
 * to the same game as the two it sits between. The black ground is not a
 * scrim: it is the frame the original's own screen transitions open on, the
 * same one `intro/gate-screen.ts` holds its prompt over.
 *
 * Pure, like every other screen module here: a `DrawOp` list against palette
 * indices, so the geometry, the centring and the bar's width at a given
 * fraction are all assertable without a canvas.
 *
 * **This is a port screen and it does not pretend otherwise.** `RB.EXE` has no
 * loading screen with a progress bar — it paints the briefing and swaps to the
 * cockpit — so nothing here is transcribed from a capture. What *is* borrowed
 * is the construction: every ring, fill index and emboss triple below comes
 * from `plate-chrome.ts`, which was measured off four real screens.
 */

import {
  type DrawOp,
  fill,
  type FontId,
  LOGICAL_HEIGHT,
  LOGICAL_WIDTH,
  type Rect,
  type TextStyle,
} from "../shell/render/draw-ops.ts";
import { embossOn, PLATE_FILL, raisedPlateOps, sunkenWellOps } from "../shell/render/plate-chrome.ts";
import { ORANGE_DARKEST, ORANGE_HIGHLIGHT, ORANGE_LABEL } from "../shell/render/ps-palette.ts";
import { centerTextX, type FontAtlas } from "../shell/render/text-metrics.ts";

/** The heading. FONT1 is the shell's own button-and-heading face. */
export const LOADING_TITLE = "PREPARING FOR TAKEOFF";
export const TITLE_FONT: FontId = "FONT1";
/** The phase line. FONT3 is what the original sets rows and prose in — smaller than the heading, which is the relationship wanted. */
export const PHASE_FONT: FontId = "FONT3";

/**
 * The plate, centred horizontally and sitting a little above centre so the bar
 * lands on the frame's optical middle rather than below it.
 */
export const PLATE: Rect = { x: 40, y: 70, w: 240, h: 58 };
/** The well the bar runs in, inset inside the plate with a plate-face margin all round. */
export const BAR_WELL: Rect = { x: 52, y: 96, w: 216, h: 12 };
/** The bar's own travel, inside the well's two rings. */
export const BAR_TRACK: Rect = { x: BAR_WELL.x + 2, y: BAR_WELL.y + 2, w: BAR_WELL.w - 4, h: BAR_WELL.h - 4 };

const TITLE_Y = 78;
const PHASE_Y = 113;
/**
 * The standing note (`BootProgress.note`), **below the plate on the black**
 * rather than on it.
 *
 * The plate runs y 70…128 and its inside is full — the title at 78, the bar
 * well at 96…108, the phase caption at 113. Squeezing a fourth line onto it
 * would have meant growing the plate, which is the one measurement on this
 * screen borrowed wholesale from `plate-chrome.ts`. Off the plate the line is
 * also unambiguously *not* part of the caption: the phase changes seven times
 * during a boot and this does not.
 */
const NOTE_Y = 138;

/**
 * The heading takes the burnt-orange engraving the Main Menu inks its **button
 * labels** in — `243/246/252`, the triple `plate-chrome.ts`'s emboss rule
 * reproduces at `B=249` and the one `intro/gate-screen.ts` already borrows for
 * the start prompt. It is the shell's own way of saying "this line is the point
 * of the screen", and against the tan plate it is the only text on it that
 * reads from across a room.
 */
const TITLE_STYLE: TextStyle = { body: ORANGE_LABEL, shadow: ORANGE_DARKEST, highlight: ORANGE_HIGHLIGHT };
/** The phase is a caption, so it takes the quieter tan emboss the plate's own body text uses. */
const PHASE_STYLE: TextStyle = embossOn(PLATE_FILL + 2);

/**
 * How wide the bar is at `fraction`.
 *
 * **Floored, and never rounded up to a visible sliver.** A bar that shows one
 * lit pixel at 0 claims progress that has not happened; the first phase reports
 * a fraction of exactly 0 and must draw nothing. At the other end the track is
 * filled edge to edge, so `ready` leaves no unlit column behind.
 */
export function barWidth(fraction: number): number {
  const clamped = Math.min(1, Math.max(0, fraction));
  return Math.floor(BAR_TRACK.w * clamped);
}

/**
 * The lit part of the bar: a solid burnt-orange body with the ramp's own
 * highlight on its top row and its darkest step on the bottom, which is how
 * every other lit element in this shell is shaded (`plate-chrome.ts`'s emboss
 * rule, read as a fill rather than as type).
 */
function barOps(fraction: number): DrawOp[] {
  const w = barWidth(fraction);
  if (w <= 0) return [];
  const { x, y, h } = BAR_TRACK;
  return [
    fill({ x, y, w, h }, ORANGE_LABEL),
    fill({ x, y, w, h: 1 }, ORANGE_HIGHLIGHT),
    fill({ x, y: y + h - 1, w, h: 1 }, ORANGE_DARKEST),
  ];
}

export interface LoadingScreenFonts {
  readonly title: FontAtlas;
  readonly phase: FontAtlas;
}

/**
 * The note's own style. It sits on **black**, not on the plate, so the tan
 * emboss `PHASE_STYLE` uses — which is three steps of the plate's own fill —
 * would be three shades of tan on a ground that has none of them and would read
 * as a smudge. The burnt-orange ramp is the shell's one legible-on-black
 * lettering (`intro/gate-screen.ts` sets its start prompt in it for the same
 * reason), and it is the same triple the title above already uses.
 */
const NOTE_STYLE: TextStyle = { body: ORANGE_LABEL, shadow: ORANGE_DARKEST, highlight: ORANGE_HIGHLIGHT };

/** The two lines, or nothing at all when the atlases have not arrived yet. */
function textOps(phase: string, note: string | undefined, fonts: LoadingScreenFonts | undefined): DrawOp[] {
  if (!fonts) return [];
  const noteOps: DrawOp[] =
    note === undefined
      ? []
      : [
          {
            kind: "text",
            font: PHASE_FONT,
            text: note,
            x: centerTextX(fonts.phase, note, LOGICAL_WIDTH / 2),
            y: NOTE_Y,
            style: NOTE_STYLE,
          },
        ];
  return [
    ...noteOps,
    {
      kind: "text",
      font: TITLE_FONT,
      text: LOADING_TITLE,
      x: centerTextX(fonts.title, LOADING_TITLE, LOGICAL_WIDTH / 2),
      y: TITLE_Y,
      style: TITLE_STYLE,
    },
    {
      kind: "text",
      font: PHASE_FONT,
      text: phase,
      x: centerTextX(fonts.phase, phase, LOGICAL_WIDTH / 2),
      y: PHASE_Y,
      style: PHASE_STYLE,
    },
  ];
}

/**
 * The whole frame. `fraction` is where `boot-progress.ts` says the bar stands;
 * `phase` is its label.
 *
 * `fonts` is optional because the plate and the bar cost three kilobytes of
 * palette and the type costs the bake manifest, and the bar is the part that
 * carries the information. Without atlases the screen draws complete except for
 * its two lines — which is why the ops are ordered plate, text, well, bar
 * rather than by layer: nothing overlaps, so dropping the text changes nothing
 * about what is drawn around it.
 */
export function loadingScreenOps(
  phase: string,
  fraction: number,
  fonts: LoadingScreenFonts | undefined,
  /** `BootProgress.note` — a standing fact under the plate, today only `RENDERER: WEBGL`. Absent on the ordinary boot. */
  note?: string,
): DrawOp[] {
  return [
    fill({ x: 0, y: 0, w: LOGICAL_WIDTH, h: LOGICAL_HEIGHT }, 0),
    ...raisedPlateOps(PLATE),
    ...textOps(phase, note, fonts),
    ...sunkenWellOps(BAR_WELL),
    ...barOps(fraction),
  ];
}
